import { FC, useEffect, useMemo, useState } from 'react';
import PaginationBar from '../../components/PaginationBar';
import { ProductMaster, fetchProductMasters } from '../../api/productMasters';
import { runSyncJob } from '../../api/syncData';

interface MastersPanelProps {
  onSelect?: (id: string) => void;
  onCreate?: () => void;
  servicesOnly?: boolean;
  onEdit?: (id: string) => void;
}

interface MastersState {
  records: ProductMaster[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const MastersPanel: FC<MastersPanelProps> = ({ onSelect, onCreate, servicesOnly = false, onEdit }) => {
  const [page, setPage] = useState(1);
  const limit = 20;
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [state, setState] = useState<MastersState>({
    records: [],
    total: 0,
    page: 1,
    limit,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const listLabel = servicesOnly ? 'services' : 'master products';
  const searchPlaceholder = servicesOnly ? 'Search services...' : 'Search master products...';
  const emptyMessage = servicesOnly ? 'No services found.' : 'No master products found.';
  const loadingMessage = servicesOnly ? 'Loading services...' : 'Loading master products...';
  const errorMessage = servicesOnly
    ? 'Failed to load services.'
    : 'Failed to load master products.';

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);
    const createdFilter = servicesOnly ? true : false;
    const isActive =
      statusFilter === 'all' ? undefined : statusFilter === 'active';

    fetchProductMasters({
      page,
      limit,
      search: debouncedSearch || undefined,
      createdByWorkshopManagement: createdFilter,
      isActive,
    })
      .then((response) => {
        if (!isMounted) {
          return;
        }
        setState({
          records: response.data,
          total: response.total,
          page: response.page,
          limit: response.limit,
          totalPages: response.totalPages,
        });
      })
      .catch((err) => {
        if (!isMounted) {
          return;
        }
        setError(err instanceof Error ? err.message : errorMessage);
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [page, limit, debouncedSearch, servicesOnly, refreshCounter, errorMessage, statusFilter]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(state.total / limit || 1)),
    [state.total, limit],
  );

  const formatType = (value: ProductMaster['type']) => {
    if (!value) {
      return 'Regular';
    }

    const label = value.replace(/[_-]+/g, ' ');
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  const triggerRefresh = () => setRefreshCounter((prev) => prev + 1);

  const handleSync = async () => {
    if (syncing) {
      return;
    }

    setSyncing(true);
    try {
      await runSyncJob('productMasters');
      triggerRefresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : `Failed to sync ${listLabel}.`;
      setError(message);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="mt-6 flex-1 rounded-2xl bg-white px-6 py-5 shadow-inner">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={searchPlaceholder}
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:w-72"
          />
        </div>

        <div className="flex items-center gap-2">
          {!servicesOnly ? (
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
              onClick={handleSync}
              disabled={syncing}
            >
              {syncing ? 'Syncing…' : 'Sync from ERP'}
            </button>
          ) : null}
          {onCreate ? (
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow hover:bg-primary/90"
              onClick={onCreate}
            >
              {'Add'}
            </button>
          ) : null}
        </div>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200">
        <table className="min-w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3 font-semibold">ERP ID</th>
              <th className="px-5 py-3 font-semibold">Name</th>
              <th className="px-5 py-3 font-semibold">Article #</th>
              <th className="px-5 py-3 font-semibold">Brand</th>
              <th className="px-5 py-3 font-semibold">Template</th>
              <th className="px-5 py-3 font-semibold">Type</th>
              <th className="px-5 py-3 font-semibold">Source</th>
              <th className="px-5 py-3 font-semibold">Updated</th>
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={10}>
                  {loadingMessage}
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td className="px-5 py-6 text-center text-danger" colSpan={10}>
                  {error}
                </td>
              </tr>
            ) : state.records.length === 0 ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={10}>
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              state.records.map((master) => (
                <tr key={master.id} className="even:bg-slate-50/60">
                  <td className="px-5 py-3 font-medium text-slate-700">{master.erpId}</td>
                  <td className="px-5 py-3 text-slate-500">{master.name}</td>
                  <td className="px-5 py-3 text-slate-500">{master.articleNumber}</td>
                  <td className="px-5 py-3 text-slate-500">{master.brand?.name ?? '—'}</td>
                  <td className="px-5 py-3 text-slate-500">{master.variantTemplate?.name ?? '—'}</td>
                  <td className="px-5 py-3 text-slate-500">{formatType(master.type)}</td>
                  <td className="px-5 py-3 text-slate-500">
                    {master.isCreatedByWorkshopManagement ? 'Workshop' : 'ERP'}
                  </td>
                  <td className="px-5 py-3 text-slate-500">
                    {new Date(master.updatedAt).toLocaleString()}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        type="button"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-primary transition hover:bg-primary/10"
                        onClick={() => onSelect?.(master.id)}
                      >
                        👁️
                      </button>
                      {servicesOnly ? (
                        <button
                          type="button"
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-100 hover:text-primary"
                          onClick={() => onEdit?.(master.id)}
                        >
                          ✎
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationBar
        page={state.page}
        totalPages={totalPages}
        onPrev={() => setPage((prev) => Math.max(1, prev - 1))}
        onNext={() => setPage((prev) => Math.min(totalPages, prev + 1))}
        onSelect={(num) => setPage(num)}
        disabled={loading}
        renderSummary={
          <span>
            Showing {(state.page - 1) * limit + Math.min(limit, state.records.length)} of {state.total}{' '}
            {listLabel}
          </span>
        }
        renderExtra={
          <span className="text-xs text-slate-400">Page {state.page} of {totalPages} (20 / page)</span>
        }
      />
    </div>
  );
};

export default MastersPanel;
